import React, { useEffect, useState } from 'react';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import Text from '@amzn/meridian/text';
import Textarea from '@amzn/meridian/textarea';
import AssociateAvatar from '../../components/AssociateAvatar';
import { getAssociateDisplayLabel } from '../../utils';
import {
  AssignmentAssociate,
  DecisionReasonCode,
  OverrideDecision,
  USER_OVERRIDE_REASON_OPTIONS,
} from '../models';

export interface UnassignAssociateModalProps {
  /** Whether the modal is open. */
  open: boolean;
  /** The assigned associate being returned to the unassigned pool. */
  associate: AssignmentAssociate | null;
  /**
   * Human-readable summary of where the associate is currently assigned
   * (e.g. "Primary → Primary line 1 → ws-01-02"). Resolved by the parent from
   * the process-path structure so this component stays presentational.
   */
  currentLocationLabel?: string;
  /** Fires when the planner cancels or dismisses the modal. */
  onClose: () => void;
  /**
   * Fires when the planner confirms the unassign. Receives the captured
   * `USER_OVERRIDE` decision (reason code + optional free text) so the parent
   * can return the associate to the pool and stamp the reason.
   */
  onSubmit: (override: OverrideDecision) => void;
}

/**
 * Unassign popup for the redesigned Station Assignment screen. Opened from an
 * occupied slot's "Unassign" action. Mirrors the `sample.html` prototype: it
 * shows the associate and their current assignment, requires a **reason** from
 * the same {@link USER_OVERRIDE_REASON_OPTIONS} list used by the Assign popup,
 * and accepts optional free-text context — so returning an associate to the
 * unassigned pool is captured as a `USER_OVERRIDE` decision just like an
 * assignment.
 *
 * All controls are Meridian (Modal/Select/Textarea/Button/Row/Column/Text)
 * plus the shared AssociateAvatar. The reason and context reset whenever the
 * modal closes.
 */
const UnassignAssociateModal: React.FC<UnassignAssociateModalProps> = ({
  open,
  associate,
  currentLocationLabel,
  onClose,
  onSubmit,
}) => {
  const [reasonCode, setReasonCode] = useState<DecisionReasonCode | undefined>(undefined);
  const [decisionReason, setDecisionReason] = useState<string>('');

  // Reset the captured reason/context whenever the modal closes so a reopen
  // starts clean.
  useEffect(() => {
    if (!open) {
      setReasonCode(undefined);
      setDecisionReason('');
    }
  }, [open]);

  const submitDisabled = !reasonCode;

  const handleSubmit = () => {
    // Guard mirrors `submitDisabled`; via the UI this never fires without one.
    /* istanbul ignore if -- defensive: Submit is disabled until a reason is chosen */
    if (!reasonCode) return;
    onSubmit({
      reasonCode,
      decisionReason: decisionReason.trim() || undefined,
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Unassign associate"
      scrollContainer="viewport"
      width="440px"
    >
      <Box data-testid="unassign-associate-modal">
        {associate && (
          <Row
            alignmentVertical="center"
            spacing="300"
            spacingInset="none none 400 none"
            data-testid="unassign-associate-info"
          >
            <AssociateAvatar
              associateName={associate.associateName}
              associateAlias={associate.associateAlias}
            />
            <Column spacing="none">
              <Text type="h300">
                {getAssociateDisplayLabel(associate.associateAlias, associate.associateName)}
              </Text>
              {currentLocationLabel && (
                <Text type="b100" color="secondary">
                  {currentLocationLabel}
                </Text>
              )}
            </Column>
          </Row>
        )}

        <Column spacing="300">
          <Text type="b200">
            This associate will be returned to the unassigned pool.
          </Text>

          <Column spacing="100">
            <Text type="b100" color="secondary">
              Reason (required)
            </Text>
            <Select
              value={reasonCode}
              onChange={setReasonCode}
              placeholder="Select reason..."
              width="100%"
              size="small"
              data-testid="unassign-reason-select"
            >
              {USER_OVERRIDE_REASON_OPTIONS.map((option) => (
                <SelectOption key={option.value} value={option.value} label={option.label} />
              ))}
            </Select>
          </Column>

          <Textarea
            value={decisionReason}
            onChange={setDecisionReason}
            placeholder="Additional context (optional)"
            rows={2}
            size="small"
            data-testid="unassign-context-textarea"
          />
        </Column>
      </Box>

      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fit">
          <Button
            type="secondary"
            size="small"
            onClick={onClose}
            data-testid="unassign-cancel-button"
          >
            Cancel
          </Button>
          <Button
            type="primary"
            size="small"
            disabled={submitDisabled}
            onClick={handleSubmit}
            data-testid="unassign-submit-button"
          >
            Unassign
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

export default UnassignAssociateModal;
